import { getProgressColor, GREEN, ORANGE, RED, YELOW_GREEN } from 'components/Timer';

/**
 * Colour for the "your turn" ring on a table preview in the multi-table strip.
 *
 * The strip carries two independent signals that both used to be green:
 *   - which table you are currently VIEWING (the selection square), and
 *   - which tables are waiting on your action (the turn ring).
 * With three tables acting at once, every card was ringed the same green and
 * the viewed table was indistinguishable. The two signals now live on different
 * objects: the ring's border COLOUR tracks the shot clock, while the viewed
 * table is marked by the dot sliding beneath the row (see selectionDot.ts). A
 * card can therefore carry both at once without either one hiding the other.
 *
 * The ramp is the seat timer's own `getProgressColor`, deliberately imported
 * rather than re-declared, so a preview in the strip can never disagree with
 * the countdown on the felt for the same table.
 */

/** Glow (outer box-shadow) paired with each ramp colour. */
export const TURN_RING_GLOW: Record<string, string> = {
  [GREEN]: 'rgba(69, 190, 38, 0.8)',
  [YELOW_GREEN]: 'rgba(190, 178, 38, 0.85)',
  [ORANGE]: 'rgba(255, 165, 0, 0.85)',
  [RED]: 'rgba(255, 45, 45, 0.9)'
};

export type TurnRingColors = { border: string; glow: string };

/**
 * Percentage of the shot clock ALREADY SPENT — the same quantity the seat timer
 * feeds its ramp, derived here from seconds remaining. Missing or nonsensical
 * clock data reads as "just started" (0) so a preview never jumps to red on bad
 * input. Clamped to 0–100.
 */
export const turnElapsedPercent = (
  secondsLeft: number | null | undefined,
  totalSeconds: number | null | undefined
): number => {
  if (typeof totalSeconds !== 'number' || !Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return 0;
  }
  if (typeof secondsLeft !== 'number' || !Number.isFinite(secondsLeft)) return 0;
  const remaining = Math.min(Math.max(secondsLeft, 0), totalSeconds);
  return ((totalSeconds - remaining) / totalSeconds) * 100;
};

/**
 * Seconds left on a table's clock, preferring the live per-second countdown but
 * falling back to the server figures it was seeded from.
 *
 * The fallback is not cosmetic. Switching tables remounts the whole per-table
 * subtree, and the countdown hook starts every mount at `null` before its first
 * tick — so a ring driven by the countdown alone painted start-of-clock GREEN
 * for a frame on every switch, whatever the real time left. That was the green
 * flash across the strip. `total − elapsed` is known synchronously on the very
 * first render, so the ring is the right colour immediately.
 */
export const turnSecondsLeft = (
  countdownSecs: number | null | undefined,
  turnClock: { totalSeconds: number; elapsedSeconds: number } | null | undefined
): number | null => {
  if (typeof countdownSecs === 'number' && Number.isFinite(countdownSecs)) return countdownSecs;
  if (!turnClock) return null;
  const { totalSeconds, elapsedSeconds } = turnClock;
  if (typeof totalSeconds !== 'number' || typeof elapsedSeconds !== 'number') return null;
  return Math.max(0, totalSeconds - elapsedSeconds);
};

/**
 * Border + glow for the turn ring, given the live countdown. Falls back to the
 * start-of-clock green when there is no usable clock, which is what the ring
 * showed before it was time-coded.
 */
export const turnRingColors = (
  secondsLeft: number | null | undefined,
  totalSeconds: number | null | undefined
): TurnRingColors => {
  const border = getProgressColor(turnElapsedPercent(secondsLeft, totalSeconds));
  return { border, glow: TURN_RING_GLOW[border] ?? TURN_RING_GLOW[GREEN] };
};
